<template>
  <div>
    <div v-if="!ocultar_titulo" class="d-flex align-center pt-3px">
      <span class="font-15 text-black font-weight-medium">
        <template v-if="dados.descricao_hub">{{ dados.descricao_hub }} - </template>

        {{ `Filial ${dados.filial}` }}

        <template v-if="dados.descricao_filial">
          {{ `- ${Helpers.primeiraLetraMaiuscula(dados.descricao_filial || '')}` }}
        </template>
      </span>

      <v-spacer />

      <v-chip :color="dados.filial_eh_destino ? 'green' : 'orange'" small outlined class="ml-2">
        <template v-if="dados.filial_eh_destino">
          <v-icon :size="18" class="mr-1">mdi-home-import-outline</v-icon>
          Destino
        </template>

        <template v-else>
          <v-icon :size="18" class="mr-1">mdi-home-export-outline</v-icon>
          Origem
        </template>
      </v-chip>
    </div>

    <table style="width: 100%">
      <template v-for="(linha, index) in linhas">
        <linha
          :linha="linha"
          :dados="dados"
          :key="index"
          :maximo-de-colunas="6"
        >
          <template #coluna.valor.cod_produto>
            {{ dados.cod_produto.length ? dados.cod_produto : '-' }}
          </template>

          <template #coluna.valor.descricao_fornecedor>
            <div v-text="`${dados.idfornecedor} - ${dados.descricao_fornecedor}`" class="text-truncate w-100" />
          </template>

          <template #coluna.valor.descricao_produto>
            <div v-text="dados.descricao_produto" class="text-truncate w-280" />
          </template>

          <template #coluna.titulo.estoque="{texto}">
            <Legenda :titulo="texto" legenda="Estoque ou saldo é a acumulação armazenada de uma determinada SKU em um ou mais locais de armazenamento">
              <span v-text="texto" />
            </Legenda>
          </template>

          <template #coluna.titulo.estoque_seguranca="{texto}">
            <Legenda :titulo="texto" legenda="O estoque de segurança, ou estoque mínimo, é uma ferramenta logística utilizada para mitigar riscos de stockout , ou quebra de estoque. Sendo possível se precaver de incertezas relacionadas à flutuações em oferta e demanda">
              <span v-text="texto" class="pr-1" />
            </Legenda>
          </template>

          <template #coluna.titulo.ponto_pedido="{texto}">
            <Legenda :titulo="texto" legenda="O ponto de pedido ou reorder point (ROP) refere-se ao momento certo para fazer uma compra de reposição do estoque, dando tempo para que uma determinada SKU possa ser ressuprida pelo fornecedor, sem que haja exposição seu estoque a ruptura">
              <span v-text="texto" />
            </Legenda>
          </template>

          <template #coluna.titulo.estoque_maximo="{texto}">
            <Legenda :titulo="texto" legenda="Estabelece a maior quantidade necessária em estoque para determinada SKU">
              <span v-text="texto" class="pr-1" />
            </Legenda>
          </template>

          <template #coluna.titulo.estoque_minimo="{texto}">
            <Legenda :titulo="texto">
              <span v-text="texto" />

              <template #legenda>
                Viés de exposição de loja. Compra-se uma quantidade necessária para melhorar a exposição dos produtos, desconsiderando a demanda de vendas.<br />
                <span class="font-weight-medium">Prós</span>: otimização na exposição da loja, gôndolas sempre cheias atraem clientes, estética potencializa a venda;<br />
                <span class="font-weight-medium">Contra</span>: excesso de produtos, muito capital imobilizado, capital inicial elevado para formação de novas filiais, análise subjetiva dependendo da experiência e bom senso do time;<br />
              </template>
            </Legenda>
          </template>

          <template #coluna.titulo.cobertura_estoque="{texto}">
            <Legenda :titulo="texto" :legenda="`${texto} ou Cobertura Instantânea é o índice utilizado para medir o tempo que o estoque, em determinado período, consegue cobrir as demandas futuras,  ou seja, o tempo que o produto levará para sair do estoque`">
              <span v-text="texto" />
            </Legenda>
          </template>

          <template #coluna.titulo.cobertura_esseg="{texto}">
            <Legenda titulo="Cobertura do Estoque de Segurança" legenda="Cobertura do Estoque de Segurança é o índice utilizado para medir o tempo que o Estoque de Segurança, em determinado período, consegue cobrir as demandas futuras,  ou seja, o tempo que o produto levará para sair do estoque">
              <span v-text="texto" />
            </Legenda>
          </template>

          <template #coluna.titulo.cobertura_pp="{texto}">
            <Legenda titulo="Cobertura do Ponto de Pedido" legenda="Cobertura do Ponto de Pedido é o índice utilizado para medir o tempo que o Ponto de Pedido, em determinado período, consegue cobrir as demandas futuras,  ou seja, o tempo que o produto levará para sair do estoque">
              <span v-text="texto" />
            </Legenda>
          </template>

          <template #coluna.titulo.cobertura_emax="{texto}">
            <Legenda titulo="Cobertura do Estoque Máximo" legenda="Cobertura do Estoque Máximo é o índice utilizado para medir o tempo que o Estoque Máximo, em determinado período, consegue cobrir as demandas futuras,  ou seja, o tempo que o produto levará para sair do estoque">
              <span v-text="texto" />
            </Legenda>
          </template>

          <template #coluna.titulo.cobertura_esseg_estoque_minimo="{texto}">
            <Legenda titulo="Cobertura do Estoque Estético" legenda="Cobertura do Estoque Estético é o índice utilizado para medir o tempo que o Estoque Estético, em determinado período, consegue cobrir as demandas futuras,  ou seja, o tempo que o produto levará para sair do estoque">
              <span v-text="texto" />
            </Legenda>
          </template>

          <template #coluna.titulo.status_produto="{texto}">
            <Legenda :titulo="texto">
              <span v-text="texto" />

              <template #legenda>
                Indicador que mede o nível do estoque, sendo classificado em 5 (cinco) níveis:<br />
                <span class="font-weight-medium">1. Excesso:</span> Saldo de estoques acima do estoque máximo;<br />
                <span class="font-weight-medium">2. Adequado:</span> Saldo de estoques dentro da normalidade, ou seja, menor que o estoque máximo, maior que o ponto de pedido;<br />
                <span class="font-weight-medium">3. Baixa Exposição a Ruptura:</span> Saldo de estoques menor ou igual ao ponto de pedido, porém a cobertura de estoques é maior que o leadtime do fornecedor;<br />
                <span class="font-weight-medium">4. Elevada Exposição a Ruptura:</span> Saldo de estoques menor ou igual ao ponto de pedido, porém a cobertura de estoques é menor que o leadtime do fornecedor;<br />
                <span class="font-weight-medium">5. Ruptura:</span> Saldo de estoques igual a zero;<br />
              </template>
            </Legenda>
          </template>

          <template #coluna.titulo.estoque_transito="{texto}">
            <span class="font-weight-medium" style="color: #f51535" v-text="texto" />
            <v-icon small color="#D60E2B" class="ml-2px">mdi-truck</v-icon>
            <Transferencia :produto="{...dados, idgrupo: idgrupo, filial: dados.filial}" />
          </template>

          <template #divisao_nivel_servico="{titulo}">
            <div class="d-flex align-center cursor-help">
              <Legenda :titulo="titulo" :legenda="NIVEL_SERVICO.descritivo">
                <span>{{titulo}}</span>
                <div class="ml-1 quadrado-13" :style="{background: NIVEL_SERVICO.cor}" />
              </Legenda>
            </div>
          </template>

          <template #coluna.valor.status_drp>
            <template v-if="dados.status_drp === 'OK'">Adequado</template>
            <template v-else-if="dados.status_drp === 'DISP DRP'">Distribuir</template>
            <template v-else>Abastecer</template>
          </template>

          <template #coluna.titulo.estoque_disponivel="{texto}">
            <Legenda :titulo="texto" :max-width="1000">
              <span v-text="texto" />

              <template #legenda>
                <p class="ma-0 mb-1">É a quantidade do saldo de estoque disponível para distribuir para outras filiais.</p>

                <span class="font-weight-medium ">Formula do cálculo: </span>

                <template v-if="!dados.pertence_mix_filial">
                  Não pertence ao mix da Filial, logo, todo o estoque({{ Helpers.formatarValor(dados.estoque_disponivel) }})
                  está disponível para distribuição
                </template>

                <template v-else>
                  ({{ removerCaracteresFormulasDRP(dados.formula_estoque_disponivel) }})

                  <template v-if="dados.tipo_drp === 2"> - (consumo medio mensal)</template>

                  <template v-else-if="dados.tipo_drp === 3">
                    <template v-if="dados.filial_cd === 'S'">
                      - (5% do {{ dados.cmm_diario > 0 ? 'consumo medio mensal' : 'estoque estetico' }})
                    </template>
                    <template v-else> - ({{ dados.cmm_diario > 0 ? 'consumo medio mensal' : 'estoque estetico' }})</template>
                  </template>

                  <template v-else> - (estoque seguranca de compra)</template>

                  = {{ Helpers.formatarValor(dados.estoque_disponivel) }}
                </template>
              </template>
            </Legenda>
          </template>

          <template #coluna.titulo.pertence_mix_filial="{texto}">
            <Legenda :titulo="texto" legenda="Informa se o produto está ou não cadastrado no Mix da Filial">
              <span v-text="texto" />
            </Legenda>
          </template>
          <template #coluna.valor.pertence_mix_filial>
            {{ dados.pertence_mix_filial ? 'SIM' : 'NÃO' }}
          </template>

          <template #coluna.valor.info_status_drp>
            <template v-if="dados.status_drp === 'OK'">Saldo de estoque está adequado</template>
            <template v-else-if="dados.status_drp === 'DISP DRP'">Saldo de estoque disponível para distribução</template>
            <template v-else>Saldo de estoque precisa ser abastecido</template>
          </template>

          <template #coluna.valor.nivel_servico>
            {{ NIVEL_SERVICO.descricao }}
          </template>

          <template #coluna.valor.descricao_nivel_servico>
            {{ NIVEL_SERVICO.descritivo }}
          </template>

          <template #coluna.titulo.nivel_estoque="{texto}">
            <Legenda :titulo="texto" legenda="Informa em percentual a quantidade de estoque de uma determinada SKU, frente aos seus parâmetros de estoque máximo e segurança">
              <span v-text="texto" />
            </Legenda>
          </template>

          <template #coluna.valor.nivel_estoque="{valor}">
            <NivelEstoqueProgressCircular :valor="valor" :status-produto="dados.status_produto" />
          </template>

          <template #coluna.valor.status_produto="{valor}">
            <div v-text="valor" :title="valor" class="text-truncate cursor-help w-90" />
          </template>

          <template #coluna.titulo.tempo_medio_ressuprimento="{texto}">
            <Legenda :titulo="texto" legenda="Ressuprimento ou tempo de reabastecimento ou Lead time, é o prazo completo para a reposição da SKU, contado desde a identificação da respectiva necessidade, passando pelo processo de reposição (planejamento e aquisição), fabricação e despacho pelo fornecedor, até o recebimento e liberação para consumo">
              <span v-text="texto" class="font-weight-medium" />
            </Legenda>
          </template>

          <template #coluna.titulo.cmm_diario="{texto}">
            <Legenda :titulo="texto" legenda="Valor calculado do quanto se espera vender no próximo mês, baseado no seu comportamento histórico">
              <span v-text="texto" />
            </Legenda>
          </template>
        </linha>
      </template>
    </table>
  </div>
</template>

<script>
import linha from "@/components/produto/detalhes/LinhaTabelaDetalhesProdutos";
import NivelEstoqueProgressCircular from "@/components/produto/detalhes/NivelEstoqueProgressCircular";
import {computed} from "vue";
import {buscarObjetoStatus} from "@/helpers";
import Transferencia from "@/components/compra/detalhesProduto/Transferencia/Transferencia";
import Legenda from "@/components/produto/detalhes/Legenda";

export default {
  name: "DetalhesProdutoFilialDrp",
  components: {
    Legenda,
    Transferencia,
    NivelEstoqueProgressCircular,
    linha
  },
  props: {
    dados: Object,
    ocultar_titulo: Boolean,
    idgrupo: {
      type: Number,
      default: null
    }
  },
  setup(props) {
    const linhas = [
      {
        colunas: [
          {texto: 'Código Interno', valor: 'idproduto'},
          {texto: 'Código Fornecedor', valor: 'cod_produto'},
          {texto: 'Nome Fornecedor', valor: 'descricao_fornecedor'},
          {texto: 'Nome Produto', valor: 'descricao_produto', colspan: 3},
        ]
      },
      {
        divider: true,
        titulo: 'Análise DRP do produto',
      },
      {
        colunas: [
          {texto: 'Pertence ao Mix', valor: 'pertence_mix_filial'},
          {texto: 'Status', valor: 'status_drp'},
          {texto: 'Necessidade DRP', valor: 'sugestao_drp', formatar: true},
          {texto: 'Disponivel para outras filiais', valor: 'estoque_disponivel', formatar: true},
          {texto: 'Descrição', valor: 'info_status_drp', colspan: 2},
        ]
      },
      {
        divider: true,
        titulo: 'Estoques e coberturas',
      },
      {
        colunas: [
          {texto: 'Nível Estoque', valor: 'nivel_estoque', rowspan: 2},
          {texto: 'Saldo Estoque', valor: 'estoque', formatar: true},
          {texto: 'Estoque Segurança', valor: 'estoque_seguranca', formatar: true},
          {texto: 'Ponto Pedido', valor: 'ponto_pedido', formatar: true},
          {texto: 'Estoque Máximo', valor: 'estoque_maximo', formatar: true},
          {texto: 'Estoque estético', valor: 'estoque_minimo', formatar: true},
        ]
      },
      {
        colunas: [
          {texto: 'Cobertura Estoque', valor: 'cobertura_estoque', formatar: true, append: 'dias'},
          {texto: 'Cobertura Eseg', valor: 'cobertura_esseg', formatar: true, append: 'dias'},
          {texto: 'Cobertura PP', valor: 'cobertura_pp', formatar: true, append: 'dias'},
          {texto: 'Cobertura EMáx', valor: 'cobertura_emax', formatar: true, append: 'dias'},
          {texto: 'Cobertura estético', valor: 'cobertura_esseg_estoque_minimo', formatar: true, append: 'dias'},
        ]
      },
      {
        colunas: [
          {texto: 'Status Produto', valor: 'status_produto'},
          {texto: 'Ressuprimento', valor: 'tempo_medio_ressuprimento', formatar: true, append: 'dias'},
          {texto: 'Projeção venda', valor: 'cmm_diario', formatar: true},
          {texto: 'Bloqueado', valor: 'estoque_bloqueado', formatar: true},
          {texto: 'Transf. entre Filiais', valor: 'estoque_transito', formatar: true},
        ]
      },
      {
        divider: true,
        titulo: 'Nível de Serviço',
        nome: 'divisao_nivel_servico'
      },
      {
        colunas: [
          {texto: 'Nível', valor: 'nivel_servico'},
          {texto: 'Descrição', valor: 'descricao_nivel_servico', colspan: 3}
        ]
      },
      {
        divider: true,
        desativar_slot: true,
      },
      {
        colunas: [
          {texto: 'Unidade Compra', valor: 'unidade_compra'},
          {texto: 'Unidade Venda', valor: 'unidade_venda'},
          {texto: 'Fator conversão', valor: 'multiplo_compra', formatar: true},
          {texto: 'Fator conversão filial', valor: 'multiplo_dist', formatar: true, colspan: 2},
        ]
      },
      {
        divider: true,
        desativar_slot: true,
      },
      {
        colunas: [
          {texto: 'Custo Aquisição', valor: 'classificacao_financeira'},
          {texto: 'Criticidade Resultado', valor: 'classificacao_criticidade'},
          {texto: 'Comprabilidade', valor: 'classificacao_comprabilidade'},
          {texto: 'Frequência Saída', valor: 'classificacao_popularidade'},
          {texto: 'Perfil Demanda', valor: 'perfil_demanda'},
        ]
      },
      {
        colunas: [
          {texto: 'Coeficiente Variação', valor: 'coeficiente_variacao', formatar: true, append: '%'},
          {texto: 'Desvio Padrão de Consumo', valor: 'desvio_padrao_consumo', formatar: true, colspan: 2},
          {texto: 'Frequência Média de Apanhe', valor: 'tempo_medio_apanhe', formatar: true, colspan: 2},
        ]
      },
    ];

    const NIVEL_SERVICO = computed(() => {
      const nivelProduto = buscarObjetoStatus(props.dados.status || '');
      return  nivelProduto[0] || {};
    });

    const removerCaracteresFormulasDRP = str => {
      if (!str || typeof str !== 'string') return '';

      return str.replaceAll('prod.estoque_min', 'estoque_estetico')
        .replaceAll('+', ' + ')
        .replaceAll('-', ' - ')
        .replaceAll('_', ' ')
        .replaceAll('p.', '')
        .replaceAll('prod.', '')
        .replaceAll('cpd.', '')
        .replaceAll('(', '')
        .replaceAll(')', '');
    }

    return {
      linhas,
      NIVEL_SERVICO,
      removerCaracteresFormulasDRP
    }
  }
}
</script>

<style scoped>

</style>
